import { useMutation, useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { ResolutionStatusChip } from "components/ResolutionStatusChip";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import {
  LQG_THRESHOLD_ACUTE_HAZARDOUS_LB,
  LQG_THRESHOLD_HAZARDOUS_LB,
} from "encamp-shared/src/constants/wasteGeneratorStatusThresholds";
import { getStatusLabel } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { stateGeneratorStatusRank } from "encamp-shared/src/waste";
import { stateDoesNotFollowFederalGeneratorStatus } from "encamp-shared/src/waste/constants";
import { gql } from "generated-graphql";
import {
  GeneratorStatusExceedanceDirection,
  GeneratorStatusExceedanceRiskLevel,
  RcraInfoFederalWasteGeneratorStatus,
  ResolutionStatus,
  StateRegulatoryStatus,
} from "generated-graphql/graphql";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  CreateGeneratorStatusChangeSubmissionsDialog,
  GeneratorStatusChangeFacility,
} from "./CreateGeneratorStatusChangeSubmissionsDialog";
import { DRAWER_PARAM, toDrawerParam } from "./drawerDeepLink";
import { drawerSectionSx, DrawerSectionHeading } from "./DrawerSection";
import { EXCEEDANCE_REFETCH, ExceedanceKey } from "./exceedanceKeys";
import { ExceedanceMetaLine } from "./ExceedanceMetaLine";
import { resolutionMethodLabel } from "./manifestCorrectionDisplay";
import { ManifestCorrectionsTable } from "./ManifestCorrectionsTable";
import { MarkResolvedDialog } from "./MarkResolvedDialog";
import { formatPeriodMonth, formatPeriodSpan } from "./periodFormat";
import { stageLabel } from "./stages";
import { stateStatusPillLabel, StatusTransition } from "./StatusTransition";

type LinkedSubmissionSummary = {
  id: string;
  title: string;
  submissionType?: string;
  status: string;
  dueDate?: string | null;
};

export type ExceedanceForDrawer = {
  facilityId: string;
  tenantId: string;
  facilityName: string;
  organization: string;
  state: string | null;
  source: string;
  periodStart: string;
  /** Latest month of the status run; equals periodStart for one month. */
  lastPeriodStart?: string | null;
  reportedFederalStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedFederalStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  /** State-axis snapshots; populated only for states with their own generator tiers. */
  reportedStateStatus?: StateRegulatoryStatus | null;
  calculatedStateStatus?: StateRegulatoryStatus | null;
  /** Non-null when the state axis mismatches (reported vs calculated). */
  stateDirection?: GeneratorStatusExceedanceDirection | null;
  changeExplanation: string;
  resolutionStatus: ResolutionStatus;
  direction?: GeneratorStatusExceedanceDirection | null;
  sustainedMonths?: number | null;
  /** Detected mismatching months folded into the run; null until the next sweep. */
  mismatchMonths?: number | null;
  /** Derived compliance-risk signal; UPWARD rows only. */
  riskLevel?: GeneratorStatusExceedanceRiskLevel | null;
  detectedAt?: string | null;
  validatedAt?: string | null;
  staffAssigneeName?: string | null;
  openSubmission?: LinkedSubmissionSummary | null;
  linkedSubmission?: LinkedSubmissionSummary | null;
};

const DETAIL_QUERY = gql(`
  query GeneratorStatusExceedanceDetail($facilityId: ID!, $source: String!, $periodStart: Date!) {
    generatorStatusExceedanceDetail(facilityId: $facilityId, source: $source, periodStart: $periodStart) {
      facilityId
      source
      periodStart
      determinationFactors
      resolution {
        id
        status
        resolutionType
        note
        resolvedAt
      }
      events {
        id
        eventType
        status
        resolutionType
        note
        actorId
        actorName
        createdAt
      }
      sources {
        source
        federalStatus
        totalHazardousLbs
        acuteLbs
      }
      calculatedMonths {
        periodStart
        federalStatus
        stateStatus
        totalHazardousLbs
        acuteLbs
        stateRegulatedLbs
        mismatch
        inRun
        siblingPeriodStart
      }
      wadmByMonth {
        periodStart
        totalWeightLbs
        itemCount
        wadmGeneratorStatus
      }
      manifestCorrections {
        id
        processedManifestId
        manifestTrackingNumber
        shippedDate
        wasteLineNumber
        field
        status
        resolutionPath
        eManifestValue
        vendorValue
        resolvedAt
        resolvedByName
      }
    }
  }
`);

const DISMISS_MUTATION = gql(`
  mutation DismissGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $reason: String!
  ) {
    dismissGeneratorStatusExceedances(exceedances: $exceedances, reason: $reason) {
      id
      status
    }
  }
`);

const REOPEN_MUTATION = gql(`
  mutation ReopenGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $note: String
  ) {
    reopenGeneratorStatusExceedances(exceedances: $exceedances, note: $note) {
      id
      status
    }
  }
`);

const VALIDATE_MUTATION = gql(`
  mutation ValidateGeneratorStatusExceedance($exceedances: [GeneratorStatusExceedanceKeyInput!]!) {
    validateGeneratorStatusExceedances(exceedances: $exceedances) {
      id
      status
    }
  }
`);

const UNVALIDATE_MUTATION = gql(`
  mutation UnvalidateGeneratorStatusExceedance($exceedances: [GeneratorStatusExceedanceKeyInput!]!) {
    unvalidateGeneratorStatusExceedances(exceedances: $exceedances) {
      id
      status
    }
  }
`);

/** Months shown in the calculated-status table before the "show all" toggle. */
const COLLAPSED_MONTHS = 8;

function statusLabel(status: string): string {
  return prettyPrintEnumValue(status);
}

/**
 * Distinct chip color per lifecycle stage so each reads uniquely,
 * matching the grid: Needs Validation = error/red, Validated · Ready for Pickup =
 * warning/orange. Undefined falls back to the chip's status default (In Flight /
 * PENDING = info/blue, Resolved = success/green, Ignored = grey).
 */
function stageRole(status: ResolutionStatus, validated: boolean): "warning" | "error" | undefined {
  if (status === ResolutionStatus.Unresolved) return validated ? "warning" : "error";
  return undefined;
}

/** Human label for a waste source. */
function sourceLabel(source: string): string {
  if (source === "eManifest") return "e-Manifest";
  if (source === "vendor") return "Vendor";
  if (source === "document") return "Document";
  return prettyPrintEnumValue(source);
}

/**
 * True when a month's LQG determination rests on acute waste alone: generating
 * >= 2.2 lbs (1 kg) of acutely hazardous waste in a month makes the facility LQG
 * regardless of total volume (40 CFR 262.13). Not flagged when total tonnage
 * independently reaches the LQG threshold — acute isn't the deciding factor there.
 * Mirrors `federalThresholdCalculator` in process-manifests.
 */
function isAcuteDriven(
  federalStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined,
  acuteLbs: number | null | undefined,
  totalLbs: number | null | undefined
): boolean {
  return (
    federalStatus === RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator &&
    acuteLbs != null &&
    acuteLbs >= LQG_THRESHOLD_ACUTE_HAZARDOUS_LB &&
    (totalLbs == null || totalLbs < LQG_THRESHOLD_HAZARDOUS_LB)
  );
}

function formatLbs(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

type Props = {
  exceedance: ExceedanceForDrawer | null;
  /** Deep-link hydration in flight: open the shell with skeletons while the row loads. */
  loading?: boolean;
  onClose: () => void;
  /** Called after a resolution action so the caller can refetch + clear state. */
  onResolved: () => void;
};

export function ExceedanceActionDrawer({
  exceedance,
  loading = false,
  onClose,
  onResolved,
}: Props) {
  const open = exceedance !== null || loading;
  const [notePanel, setNotePanel] = useState<"dismiss" | "reset" | null>(null);
  const [noteText, setNoteText] = useState("");
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [markResolvedOpen, setMarkResolvedOpen] = useState(false);

  const { data, loading: detailLoading } = useQuery(DETAIL_QUERY, {
    variables: exceedance
      ? {
          facilityId: exceedance.facilityId,
          source: exceedance.source,
          periodStart: exceedance.periodStart,
        }
      : { facilityId: "", source: "", periodStart: "" },
    skip: !exceedance,
    fetchPolicy: "cache-and-network",
  });

  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });
  const [reopen, { loading: reopening }] = useMutation(REOPEN_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });

  const [validate, { loading: validating }] = useMutation(VALIDATE_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });
  const [unvalidate, { loading: unvalidating }] = useMutation(UNVALIDATE_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });

  const alerts = useAlerts();

  const busy = dismissing || reopening || validating || unvalidating;

  // Validate keeps the drawer open (you often validate, then file). The parent passes
  // a snapshot, so we overlay the changed field locally and refetch the grid behind the
  // scenes; the overlay resets when a different row opens.
  const [overlay, setOverlay] = useState<{
    validatedAt?: string | null;
  }>({});
  // The months table collapses long histories behind a toggle (no nested scroll
  // region — the drawer already scrolls). Collapse again when a new row opens.
  const [showAllMonths, setShowAllMonths] = useState(false);
  useEffect(() => {
    setOverlay({});
    setShowAllMonths(false);
  }, [exceedance?.facilityId, exceedance?.source, exceedance?.periodStart]);
  const effectiveValidatedAt =
    "validatedAt" in overlay ? overlay.validatedAt : (exceedance?.validatedAt ?? null);

  const exceedanceKey = useMemo<ExceedanceKey | null>(
    () =>
      exceedance
        ? {
            facilityId: exceedance.facilityId,
            source: exceedance.source,
            periodStart: exceedance.periodStart,
            reportedFederalStatus: exceedance.reportedFederalStatus ?? null,
            calculatedFederalStatus: exceedance.calculatedFederalStatus ?? null,
          }
        : null,
    [exceedance]
  );

  const detail = data?.generatorStatusExceedanceDetail;
  // How the row was resolved, in the same words the CSV export uses.
  const resolutionMethod = resolutionMethodLabel(detail?.resolution?.resolutionType);
  const federalFactors = (detail?.determinationFactors as { federal?: Record<string, number> })
    ?.federal;

  // Builds a same-page href that swaps the drawer to a sibling exceedance via its
  // deep-link param, preserving every other query param (omnisearch filters etc.).
  // The dashboard hydrates the new key and swaps the drawer in place.
  const [pageSearchParams] = useSearchParams();
  const siblingDrawerSearch = (siblingPeriodStart: string): string => {
    const next = new URLSearchParams(pageSearchParams);
    if (exceedance) {
      next.set(
        DRAWER_PARAM,
        toDrawerParam({
          facilityId: exceedance.facilityId,
          source: exceedance.source,
          periodStart: siblingPeriodStart,
        })
      );
    }
    return `?${next.toString()}`;
  };
  // Acute-driven determination: the run's latest month is LQG on acute waste alone.
  // Called out prominently — a facility at a few hundred total lbs reads as
  // obviously-not-LQG unless the 1 kg acute rule is front and center.
  const factorAcuteLbs = federalFactors?.["Acute Waste (lbs)"] ?? null;
  const factorTotalLbs = federalFactors?.["Total Hazardous Waste (lbs)"] ?? null;
  const acuteDriven = isAcuteDriven(
    exceedance?.calculatedFederalStatus,
    factorAcuteLbs,
    factorTotalLbs
  );
  // On a state-axis row, an equal-rank federal pair reads "no discrepancy"
  // instead of a same-to-same arrow. The shared state ladder ranks federal tier
  // names too, and rates non-tiers (Not a Generator, null) equal at 0.
  const federalAgrees =
    Boolean(exceedance?.stateDirection) &&
    stateGeneratorStatusRank(exceedance?.reportedFederalStatus ?? null) ===
      stateGeneratorStatusRank(exceedance?.calculatedFederalStatus ?? null);
  const hasSourcesSection = Boolean(
    (detail && detail.sources.length > 0) || federalFactors || exceedance?.changeExplanation
  );
  // The by-month table shows the state axis only where a state has its own tiers.
  const showStateColumns = stateDoesNotFollowFederalGeneratorStatus(exceedance?.state);

  const closeAndReset = () => {
    setNotePanel(null);
    setNoteText("");
    onClose();
  };

  const runDismiss = async () => {
    if (!exceedanceKey) return;
    try {
      await dismiss({ variables: { exceedances: [exceedanceKey], reason: noteText } });
      alerts.success("Discrepancy dismissed.");
      setNotePanel(null);
      setNoteText("");
      onResolved();
    } catch (e) {
      alerts.error("Failed to dismiss the discrepancy.", e);
    }
  };

  // Reopen/reset back to UNRESOLVED — also unlinks any linked submission. The
  // optional reason is recorded on the audit event.
  const runReset = async () => {
    if (!exceedanceKey) return;
    try {
      await reopen({ variables: { exceedances: [exceedanceKey], note: noteText || undefined } });
      alerts.success(linkedOrOpenSubmission ? "Unlinked & reset to unresolved." : "Reopened.");
      setNotePanel(null);
      setNoteText("");
      onResolved();
    } catch (e) {
      alerts.error("Failed to reset the discrepancy.", e);
    }
  };

  // Validate (TPM) — marks ready for pickup; keeps the drawer open so you can then
  // file. Mirrors the bulk "Validate (mark ready for pickup)" action.
  const runValidate = async () => {
    if (!exceedanceKey) return;
    try {
      await validate({ variables: { exceedances: [exceedanceKey] } });
      alerts.success("Validated — ready for pickup.");
      setOverlay((o) => ({ ...o, validatedAt: new Date().toISOString() }));
    } catch (e) {
      alerts.error("Failed to validate the discrepancy.", e);
    }
  };

  // Un-validate (TPM) — undo a validation applied in error; the row drops back to
  // "Needs Validation". Keeps the drawer open so the footer re-renders to the triage
  // fork, mirroring runValidate (which keeps it open to then file).
  const runUnvalidate = async () => {
    if (!exceedanceKey) return;
    try {
      await unvalidate({ variables: { exceedances: [exceedanceKey] } });
      alerts.success("Validation undone — back to needs validation.");
      setOverlay((o) => ({ ...o, validatedAt: null }));
    } catch (e) {
      alerts.error("Failed to undo the validation.", e);
    }
  };

  const isResolved =
    exceedance?.resolutionStatus === ResolutionStatus.Resolved ||
    exceedance?.resolutionStatus === ResolutionStatus.Ignored;
  const isUnresolved = exceedance?.resolutionStatus === ResolutionStatus.Unresolved;
  const isValidated = isUnresolved && Boolean(effectiveValidatedAt);
  const linkedOrOpenSubmission = exceedance?.linkedSubmission ?? exceedance?.openSubmission ?? null;
  // A linked/open submission means the reset also unlinks it.
  const resetLabel = linkedOrOpenSubmission ? "Unlink & reset" : "Reopen";

  const facilityForFiling: GeneratorStatusChangeFacility[] = exceedance
    ? [
        {
          facilityId: exceedance.facilityId,
          tenantId: exceedance.tenantId,
          facilityName: exceedance.facilityName,
          organization: exceedance.organization,
          state: exceedance.state,
          periodStart: exceedance.periodStart,
        },
      ]
    : [];

  // Footer actions are stage-specific. Ownership is NOT here — it lives in the header Owner
  // control. Each stage shows the one decision it's asking for; escapes demote to quiet links.
  const renderFooterActions = () => {
    if (notePanel) {
      return (
        <Stack spacing={1}>
          <TextField
            label={
              notePanel === "dismiss"
                ? "Reason (why is this not a discrepancy?)"
                : `Reason (optional)${linkedOrOpenSubmission ? " — this unlinks the submission" : ""}`
            }
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            size="small"
            multiline
            minRows={2}
            autoFocus
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setNotePanel(null)} disabled={busy}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={notePanel === "dismiss" ? runDismiss : runReset}
              loading={notePanel === "dismiss" ? dismissing : reopening}
              disabled={busy || (notePanel === "dismiss" && !noteText.trim())}
            >
              {notePanel === "dismiss" ? "Dismiss discrepancy" : "Reset to unresolved"}
            </LoadingButton>
          </Stack>
        </Stack>
      );
    }

    if (isResolved) {
      return (
        <Button variant="outlined" onClick={() => setNotePanel("reset")} disabled={busy}>
          Reopen
        </Button>
      );
    }

    // Needs Validation — the TPM triage fork: one decision, two opposite outcomes.
    if (isUnresolved && !isValidated) {
      return (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Is this a real discrepancy?
          </Typography>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              fullWidth
              variant="contained"
              onClick={runValidate}
              loading={validating}
              disabled={busy}
            >
              Validate
            </LoadingButton>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={() => setNotePanel("dismiss")}
              disabled={busy}
            >
              Dismiss
            </Button>
          </Stack>
          <Button
            size="small"
            sx={{ alignSelf: "flex-start" }}
            onClick={() => setMarkResolvedOpen(true)}
            disabled={busy}
          >
            Already corrected elsewhere? Mark resolved
          </Button>
        </Stack>
      );
    }

    // Validated · Ready for Pickup — the forward action is filing; the rest are quiet escapes.
    if (isValidated) {
      return (
        <Stack spacing={1}>
          <Button variant="contained" onClick={() => setFileDialogOpen(true)} disabled={busy}>
            File notification
          </Button>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button size="small" onClick={() => setMarkResolvedOpen(true)} disabled={busy}>
              Mark resolved
            </Button>
            <LoadingButton
              size="small"
              onClick={runUnvalidate}
              loading={unvalidating}
              disabled={busy}
            >
              Un-validate
            </LoadingButton>
            <Button
              size="small"
              color="warning"
              onClick={() => setNotePanel("dismiss")}
              disabled={busy}
            >
              Dismiss
            </Button>
          </Stack>
        </Stack>
      );
    }

    // In flight (PENDING) — tracking the linked submission.
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button variant="outlined" onClick={() => setMarkResolvedOpen(true)} disabled={busy}>
          Mark resolved
        </Button>
        <Button variant="outlined" onClick={() => setNotePanel("reset")} disabled={busy}>
          {resetLabel}
        </Button>
      </Stack>
    );
  };

  const footer = exceedance ? <Stack spacing={1.5}>{renderFooterActions()}</Stack> : null;

  return (
    <>
      <RightDrawerShell
        open={open}
        onClose={closeAndReset}
        // Wider than the shell default: the month-by-month and manifest-corrections
        // tables carry six columns plus a link-out, and were losing the right-hand
        // ones at 672.
        width={{ xs: "95%", sm: 760, md: 1000 }}
        // Freeze the page behind the drawer: its month history can scroll far
        // longer than the grid, and two live scrollbars side by side made it
        // easy to scroll the wrong surface.
        disableScrollLock={false}
        title={
          exceedance ? (
            // Title doubles as a facility hyperlink so staff can jump to the
            // facility without closing the drawer — new tab via the little
            // external-link icon. (Deliberately the only facility link; the
            // grid's Facility column is plain text.)
            <Link
              to={`/o/${exceedance.tenantId}/waste/facilities/${exceedance.facilityId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {exceedance.facilityName}
              <OpenInNew fontSize="small" />
            </Link>
          ) : (
            <Skeleton width={220} />
          )
        }
        footer={footer}
      >
        {!exceedance && loading && (
          // Deep-link hydration: the row is still loading — sketch the drawer's
          // shape (status row, discrepancy block, months table) instead of a
          // dead pane.
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="rounded" width={140} height={24} />
              <Skeleton width={180} />
            </Stack>
            <Skeleton width="70%" />
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={180} />
          </Stack>
        )}
        {exceedance && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ResolutionStatusChip
                status={exceedance.resolutionStatus}
                label={stageLabel({ ...exceedance, validatedAt: effectiveValidatedAt })}
                role={stageRole(exceedance.resolutionStatus, isValidated)}
              />
              {/* Compliance-risk badge: a sustained/recurring exceedance
                  escalates the longer its status run goes unaddressed. */}
              {exceedance.riskLevel && (
                <StatusChip
                  color={
                    exceedance.riskLevel === GeneratorStatusExceedanceRiskLevel.High
                      ? "error"
                      : "warning"
                  }
                  label={
                    exceedance.riskLevel === GeneratorStatusExceedanceRiskLevel.High
                      ? "High risk"
                      : "Elevated risk"
                  }
                />
              )}
              {/* The LQG determination rests on the 1 kg acute rule, not tonnage —
                  surfaced at the top so it can't be missed while triaging. */}
              {acuteDriven && (
                <Tooltip
                  title={`≥ ${LQG_THRESHOLD_ACUTE_HAZARDOUS_LB} lbs (1 kg) of acutely hazardous waste in a month makes a facility LQG regardless of total volume.`}
                >
                  <span>
                    <StatusChip color="error" label="Acute waste–driven" />
                  </span>
                </Tooltip>
              )}
              <Typography variant="body2" color="text.secondary">
                {exceedance.organization} · {exceedance.state ?? "—"} ·{" "}
                {formatPeriodSpan(exceedance)}
              </Typography>
            </Stack>

            <ExceedanceMetaLine
              direction={exceedance.direction}
              mismatchMonths={exceedance.mismatchMonths}
              sustainedMonths={exceedance.sustainedMonths}
              detectedAt={exceedance.detectedAt}
              validatedAt={effectiveValidatedAt}
              resolutionMethod={resolutionMethod}
              staffAssigneeName={exceedance.staffAssigneeName}
            />

            {/* Discrepancy: one labeled row per axis, reported → calculated as the
                same tinted pill the grid shows; an agreeing axis says so in muted
                text. The reconciliation explainer is boilerplate — info icon. */}
            <Box sx={drawerSectionSx}>
              <Stack direction="row" alignItems="center">
                <DrawerSectionHeading sx={{ mb: 0 }}>Potential discrepancy</DrawerSectionHeading>
                <InfoIconTooltip
                  ariaLabel="How the calculated status is determined"
                  tooltip="Calculated status is reconciled — the highest generator status across the latest month's e-Manifest, vendor & reviewed-document data (no weight lost)."
                />
              </Stack>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {exceedance.stateDirection && (
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ width: 72, flexShrink: 0 }}
                    >
                      {exceedance.state ?? "State"} state
                    </Typography>
                    <StatusTransition
                      oldStatus={exceedance.reportedStateStatus}
                      newStatus={exceedance.calculatedStateStatus}
                      rank={stateGeneratorStatusRank}
                      label={stateStatusPillLabel}
                    />
                  </Stack>
                )}
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ width: 72, flexShrink: 0 }}
                  >
                    Federal
                  </Typography>
                  {federalAgrees ? (
                    <Typography variant="body2" color="text.secondary">
                      No discrepancy (reported{" "}
                      {exceedance.reportedFederalStatus
                        ? statusLabel(exceedance.reportedFederalStatus)
                        : "none"}
                      )
                    </Typography>
                  ) : (
                    <StatusTransition
                      oldStatus={exceedance.reportedFederalStatus}
                      newStatus={exceedance.calculatedFederalStatus}
                    />
                  )}
                </Stack>
              </Stack>
              {acuteDriven && factorAcuteLbs != null && (
                <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                  <strong>Driven by acute hazardous waste.</strong> {formatLbs(factorAcuteLbs)} lbs
                  of acute waste this month — at or above the {LQG_THRESHOLD_ACUTE_HAZARDOUS_LB} lbs
                  (1 kg) acute threshold, which makes the facility LQG on its own
                  {factorTotalLbs != null
                    ? `; total volume (${formatLbs(factorTotalLbs)} lbs) is below the ${LQG_THRESHOLD_HAZARDOUS_LB.toLocaleString("en-US")} lbs LQG tonnage threshold`
                    : ""}
                  .
                </Alert>
              )}
            </Box>

            {/* Evidence behind the latest month: per-source breakdown and the
                waste amounts the calculator ranked. */}
            {hasSourcesSection && (
              <Box sx={drawerSectionSx}>
                <DrawerSectionHeading>
                  Sources ·{" "}
                  {formatPeriodMonth(exceedance.lastPeriodStart ?? exceedance.periodStart)}
                </DrawerSectionHeading>
                {detail && detail.sources.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Source</TableCell>
                        <TableCell>Calculated</TableCell>
                        <TableCell align="right">Total HW (lbs)</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.sources.map((s) => {
                        // Null-safe: on a state-axis row no source "drove" a federal
                        // status, so nothing gets the chip.
                        const used =
                          s.federalStatus != null &&
                          s.federalStatus === exceedance.calculatedFederalStatus;
                        const sourceAcute = isAcuteDriven(
                          s.federalStatus,
                          s.acuteLbs,
                          s.totalHazardousLbs
                        );
                        return (
                          <TableRow key={s.source}>
                            <TableCell sx={{ fontWeight: used ? 600 : 400 }}>
                              {sourceLabel(s.source)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: used ? 600 : 400 }}>
                              {s.federalStatus ? statusLabel(s.federalStatus) : "—"}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "text.secondary" }}>
                              {s.totalHazardousLbs != null ? formatLbs(s.totalHazardousLbs) : "—"}
                              {/* Only worth a mention when acute mass is present. */}
                              {s.acuteLbs != null && s.acuteLbs > 0
                                ? ` (${formatLbs(s.acuteLbs)} acute)`
                                : ""}
                            </TableCell>
                            <TableCell sx={{ py: 0.25 }}>
                              {used && (
                                <StatusChip
                                  color={sourceAcute ? "error" : "info"}
                                  label={sourceAcute ? "drove status · acute" : "drove status"}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
                {/* Document-dominant facilities: the reconciled status came from reviewed
                  manifest-document data, which isn't persisted per-source — so no row
                  above is marked as driving the status. Say so, rather than leave the
                  breakdown looking incomplete. */}
                {detail &&
                  exceedance.calculatedFederalStatus &&
                  !detail.sources.some(
                    (s) => s.federalStatus === exceedance.calculatedFederalStatus
                  ) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Reconciled from reviewed manifest-document data (not shown per-source above).
                    </Typography>
                  )}
                {federalFactors && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ letterSpacing: 0.6, lineHeight: 1 }}
                    >
                      Determination factors ·{" "}
                      {formatPeriodMonth(exceedance.lastPeriodStart ?? exceedance.periodStart)}
                    </Typography>
                    <Table size="small" sx={{ mt: 0.5 }}>
                      <TableBody>
                        {Object.entries(federalFactors).map(([k, v]) => {
                          // Bold the acute factor when it (alone) drove the LQG status.
                          const droveStatus = acuteDriven && k === "Acute Waste (lbs)";
                          return (
                            <TableRow key={k}>
                              <TableCell
                                sx={{
                                  border: 0,
                                  py: 0.25,
                                  color: droveStatus ? "warning.main" : "text.secondary",
                                  fontWeight: droveStatus ? 600 : 400,
                                }}
                              >
                                {k}
                              </TableCell>
                              <TableCell
                                sx={{ border: 0, py: 0.25, fontWeight: droveStatus ? 600 : 400 }}
                                align="right"
                              >
                                {v.toLocaleString("en-US", { maximumFractionDigits: 1 })}
                                {droveStatus ? " — drove LQG" : ""}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}
                {exceedance.changeExplanation && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Month over month: {exceedance.changeExplanation}
                  </Typography>
                )}
              </Box>
            )}

            {/* The detail query fills in the per-source breakdown, month history,
                and audit trail. Its first load sketches those sections so the
                drawer's shape doesn't pop in after the fact. */}
            {!detail && detailLoading && (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={88} />
                <Skeleton variant="rounded" height={160} />
              </Stack>
            )}

            {/* Month-by-month calculated statuses — the audit trail behind the
                consolidated row. Bold + the chip mark the months that folded into
                THIS exceedance (same calculated status); other mismatching months
                belong to sibling dashboard rows at a different status. */}
            {detail && detail.calculatedMonths.length > 0 && (
              <Box sx={drawerSectionSx}>
                <DrawerSectionHeading>Calculated status by month</DrawerSectionHeading>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Months at this discrepancy&apos;s calculated status roll up into the row
                  you&apos;re viewing; months at a different mismatched status appear as their own
                  dashboard row.
                </Typography>
                {/* No inner scroll region — a nested scrollbar inside the drawer's
                    own scroll reads as a "double scrollbar". Long histories collapse
                    behind a show-all toggle instead. */}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell>Federal</TableCell>
                      {showStateColumns && <TableCell>State</TableCell>}
                      <TableCell align="right">Total HW (lbs)</TableCell>
                      {showStateColumns && <TableCell align="right">State-reg (lbs)</TableCell>}
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(showAllMonths
                      ? detail.calculatedMonths
                      : detail.calculatedMonths.slice(0, COLLAPSED_MONTHS)
                    ).map((m) => {
                      // Run-membership cell: informational, not a warning — plain
                      // text marks this row's own months (the bold row already
                      // carries the emphasis); for a month that mismatches under a
                      // DIFFERENT status, cross-link to the sibling row's drawer
                      // when it's materialized, plain text when it isn't (yet).
                      let runCell: ReactNode = null;
                      if (m.inRun) {
                        runCell = (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            this discrepancy
                          </Typography>
                        );
                      } else if (m.mismatch) {
                        runCell = m.siblingPeriodStart ? (
                          <MuiLink
                            component={Link}
                            to={{ search: siblingDrawerSearch(m.siblingPeriodStart) }}
                            variant="caption"
                            noWrap
                          >
                            separate discrepancy
                          </MuiLink>
                        ) : (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            separate discrepancy
                          </Typography>
                        );
                      }
                      const monthAcute = isAcuteDriven(
                        m.federalStatus,
                        m.acuteLbs,
                        m.totalHazardousLbs
                      );
                      return (
                        <TableRow key={m.periodStart}>
                          <TableCell sx={{ fontWeight: m.inRun ? 600 : 400 }}>
                            {formatPeriodMonth(m.periodStart)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: m.inRun ? 600 : 400,
                              color: m.mismatch ? "text.primary" : "text.secondary",
                            }}
                          >
                            {m.federalStatus ? statusLabel(m.federalStatus) : "—"}
                            {/* The month is LQG on the 1 kg acute rule, not tonnage. */}
                            {monthAcute && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ color: "warning.main", fontWeight: 600, ml: 0.5 }}
                              >
                                · acute
                              </Typography>
                            )}
                          </TableCell>
                          {showStateColumns && (
                            <TableCell sx={{ fontWeight: m.inRun ? 600 : 400 }}>
                              {m.stateStatus ? stateStatusPillLabel(m.stateStatus) : "—"}
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ color: "text.secondary" }}>
                            {m.totalHazardousLbs != null ? formatLbs(m.totalHazardousLbs) : "—"}
                            {m.acuteLbs != null && m.acuteLbs > 0
                              ? ` (${formatLbs(m.acuteLbs)} acute)`
                              : ""}
                          </TableCell>
                          {showStateColumns && (
                            <TableCell align="right" sx={{ color: "text.secondary" }}>
                              {m.stateRegulatedLbs != null ? formatLbs(m.stateRegulatedLbs) : "—"}
                            </TableCell>
                          )}
                          <TableCell sx={{ py: 0.25 }}>{runCell}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {detail.calculatedMonths.length > COLLAPSED_MONTHS && (
                  <Button size="small" onClick={() => setShowAllMonths((v) => !v)} sx={{ mt: 0.5 }}>
                    {showAllMonths
                      ? "Show fewer"
                      : `Show all ${detail.calculatedMonths.length} months`}
                  </Button>
                )}
              </Box>
            )}

            {/* Linked submission — an explicitly linked submission (any type) or
                the auto-detected open notification driving the status. */}
            {linkedOrOpenSubmission && (
              <Box sx={drawerSectionSx}>
                <DrawerSectionHeading>Linked submission</DrawerSectionHeading>
                <MuiLink
                  component={Link}
                  to={`/o/${exceedance.tenantId}/waste/submissions/${linkedOrOpenSubmission.id}/edit`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  <OpenInNew fontSize="small" />
                  {linkedOrOpenSubmission.title} ({getStatusLabel(linkedOrOpenSubmission.status)})
                </MuiLink>
              </Box>
            )}

            {/* Manifest corrections cited as the resolution — only ever present on
                a MANIFEST_CORRECTION resolution. */}
            <ManifestCorrectionsTable
              corrections={detail?.manifestCorrections ?? []}
              tenantId={exceedance.tenantId}
            />

            {/* WADM accumulation — only rendered when the facility actually has
                hazardous WADM data. The WADM feed is Amazon-only, so this section
                is hidden entirely (no header, no empty state) for everyone else. */}
            {detail && detail.wadmByMonth.length > 0 && (
              <Box sx={drawerSectionSx}>
                <DrawerSectionHeading>
                  WADM — hazardous waste accumulation (month by month)
                </DrawerSectionHeading>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Weight (lbs)</TableCell>
                      <TableCell align="right">Items</TableCell>
                      <TableCell>WADM status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.wadmByMonth.map((m) => (
                      <TableRow key={m.periodStart}>
                        <TableCell>{formatPeriodMonth(m.periodStart)}</TableCell>
                        <TableCell align="right">
                          {m.totalWeightLbs.toLocaleString("en-US", { maximumFractionDigits: 1 })}
                        </TableCell>
                        <TableCell align="right">{m.itemCount}</TableCell>
                        <TableCell>{m.wadmGeneratorStatus ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* History */}
            {detail && detail.events.length > 0 && (
              <Box sx={drawerSectionSx}>
                <DrawerSectionHeading>Resolution history</DrawerSectionHeading>
                <Stack spacing={0.5}>
                  {detail.events.map((ev) => (
                    <Typography key={ev.id} variant="caption" color="text.secondary">
                      {new Date(ev.createdAt).toLocaleString("en-US")} — {statusLabel(ev.eventType)}
                      {" by "}
                      <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                        {ev.actorName ?? "—"}
                      </Box>
                      {ev.note ? `: ${ev.note}` : ""}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {exceedance.resolutionStatus === ResolutionStatus.Pending && (
              <Alert severity="info" variant="outlined">
                A notification has been filed for this facility — this discrepancy is being worked.
              </Alert>
            )}
          </Stack>
        )}
      </RightDrawerShell>

      <CreateGeneratorStatusChangeSubmissionsDialog
        open={fileDialogOpen}
        onClose={() => setFileDialogOpen(false)}
        facilities={facilityForFiling}
        onSuccess={() => {
          setFileDialogOpen(false);
          onResolved();
        }}
      />

      {exceedanceKey && exceedance && (
        <MarkResolvedDialog
          open={markResolvedOpen}
          onClose={() => setMarkResolvedOpen(false)}
          exceedanceKey={exceedanceKey}
          facilityId={exceedance.facilityId}
          tenantId={exceedance.tenantId}
          // Seeds the picker so re-resolving edits the cited set instead of
          // rebuilding it from memory with nothing on screen to compare against.
          citedCorrectionIds={(detail?.manifestCorrections ?? []).map((c) => c.id)}
          onResolved={() => {
            setMarkResolvedOpen(false);
            onResolved();
          }}
        />
      )}
    </>
  );
}
